import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Loader2, AlertCircle } from 'lucide-react'
import { saveContent, type ContentEntity } from '../../lib/api/content'
import { Switch } from './ui'

/**
 * Create a new "Hotel convenzionato" (convention). Mirrors the app's proven create path
 * (RateEditDrawer / TransportEditDrawer): POST a blank-id entity with the Italian elements,
 * which the backend ContentSaveAction turns into a new record. The discount is set separately
 * in Convenzioni → Scontistica, so here we only need name / description / enabled.
 */
export function ConventionCreateModal({
  open,
  onClose,
  listKey,
}: {
  open: boolean
  onClose: () => void
  listKey: readonly unknown[]
}) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [err, setErr] = useState<string | null>(null)

  function reset() {
    setName('')
    setDescription('')
    setEnabled(true)
    setErr(null)
  }

  const create = useMutation({
    mutationFn: () => {
      const entity: ContentEntity = {
        id: '',
        modelId: 'convention',
        elements: { name: name.trim(), description: description.trim(), enabled },
      }
      return saveContent('convention', entity, 'it')
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: listKey })
      reset()
      onClose()
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'Salvataggio non riuscito. Riprova.'),
  })

  if (!open) return null
  const canSave = name.trim().length > 0 && !create.isPending
  const close = () => {
    if (create.isPending) return
    reset()
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-coal/40 backdrop-blur-[1px]" onClick={close} />
      <div className="relative w-full max-w-md overflow-hidden rounded-card border border-line bg-paper shadow-pop">
        <header className="flex items-center justify-between border-b border-line bg-card px-5 py-3.5">
          <h3 className="font-display text-[16px] font-bold text-coal">Aggiungi convenzione</h3>
          <button onClick={close} className="rounded-lg p-1.5 text-muted hover:bg-paper hover:text-coal">
            <X className="h-[18px] w-[18px]" />
          </button>
        </header>

        <div className="space-y-4 px-5 py-4">
          <div>
            <label className="mb-1 block text-[12px] font-semibold uppercase tracking-wide text-muted">Nome hotel *</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canSave) create.mutate()
              }}
              placeholder="Es. Hotel Rialto"
              className="h-10 w-full rounded-lg border border-line bg-card px-3 text-[14px] text-coal outline-none focus:border-verdigris"
            />
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-semibold uppercase tracking-wide text-muted">Descrizione</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Note (facoltativo)"
              className="w-full resize-none rounded-lg border border-line bg-card px-3 py-2 text-[14px] text-coal outline-none focus:border-verdigris"
            />
          </div>
          <label className="flex items-center justify-between rounded-lg border border-line bg-card px-3 py-2.5">
            <span className="text-[13px] font-semibold text-slate">Attiva subito</span>
            <Switch on={enabled} onClick={() => setEnabled((v) => !v)} label="Attiva convenzione" />
          </label>

          {err && (
            <p className="flex items-start gap-1.5 text-[12.5px] text-venetian">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {err}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-line bg-card px-5 py-3">
          <button
            onClick={close}
            disabled={create.isPending}
            className="rounded-lg border border-line bg-card px-4 py-2 text-[13px] font-semibold text-slate hover:border-faint disabled:opacity-60"
          >
            Annulla
          </button>
          <button
            onClick={() => create.mutate()}
            disabled={!canSave}
            className="inline-flex items-center gap-2 rounded-lg bg-verdigris px-4 py-2 text-[13px] font-semibold text-white hover:bg-verdigris-700 disabled:opacity-60"
          >
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Salva
          </button>
        </footer>
      </div>
    </div>
  )
}
